import React, { useState, useEffect } from 'react';
import io from 'socket.io-client';
import './App.css';
import MapComponent from './components/MapComponent';
import BoatSelector from './components/BoatSelector';
import MobileControls from './components/MobileControls';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import Dashboard from './components/dashboard/Dashboard';
import BoatRegistry from './components/admin/BoatRegistry';
import UserManagement from './components/admin/UserManagement';
import NetworkStatus from './components/NetworkStatus';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BACKEND_URL } from './config';

function AuthenticatedApp() {
  const [socket, setSocket] = useState(null);
  const [boats, setBoats] = useState([]);
  const [selectedBoat, setSelectedBoat] = useState(null);
  const [boatRoute, setBoatRoute] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isTracking, setIsTracking] = useState(false);
  const [currentBoatId, setCurrentBoatId] = useState('');
  const [viewMode, setViewMode] = useState('auto'); // 'auto', 'desktop', or 'mobile'
  const [watchPositionId, setWatchPositionId] = useState(null);
  const [activeTab, setActiveTab] = useState('map'); // 'map', 'dashboard', 'boats', 'users'
  const [lastRefreshTime, setLastRefreshTime] = useState(Date.now());

  const { user, isAdmin, logout } = useAuth();

  // Initialize socket connection
  useEffect(() => {
    const newSocket = io(BACKEND_URL);
    setSocket(newSocket);

    // Check if we're on a mobile device
    const checkMobile = () => {
      const mobileDetected = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobileDetected);
      
      // Set default view mode based on device
      if (localStorage.getItem('viewMode')) {
        setViewMode(localStorage.getItem('viewMode'));
      } else {
        setViewMode(mobileDetected ? 'mobile' : 'desktop');
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);

    // Clean up on unmount
    return () => {
      if (newSocket) newSocket.disconnect();
      window.removeEventListener('resize', checkMobile);
      
      // Clear any active geolocation watches
      if (watchPositionId !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchPositionId);
      }
    };
  }, [watchPositionId]);

  // Fetch boats when socket is connected or when refresh is triggered
  useEffect(() => {
    if (!socket) return;

    // Listen for real-time location updates
    socket.on('locationUpdate', (newLocation) => {
      setBoats(prev => {
        const newBoats = [...prev];
        const boatIndex = newBoats.findIndex(b => b.boatId === newLocation.boatId);
        
        if (boatIndex >= 0) {
          newBoats[boatIndex].lastLocation = newLocation;
        } else {
          newBoats.push({ boatId: newLocation.boatId, lastLocation: newLocation });
        }
        
        return newBoats;
      });

      // Update route if this is the currently selected boat
      if (selectedBoat === newLocation.boatId) {
        setBoatRoute(prev => [...prev, newLocation]);
      }
    });

    // Fetch boats data on initial load or refresh
    fetchBoats();

    return () => {
      socket.off('locationUpdate');
    };
  }, [socket, selectedBoat, lastRefreshTime]);

  // Fetch boat route when a boat is selected
  useEffect(() => {
    if (selectedBoat) {
      fetchBoatRoute(selectedBoat);
    } else {
      setBoatRoute([]);
    }
  }, [selectedBoat]);

  // Add a new useEffect to refresh boats when map tab is activated
  useEffect(() => {
    if (activeTab === 'map') {
      // Force a refresh of boat data when returning to map tab
      refreshBoats();
    }
  }, [activeTab]);

  const fetchBoats = async () => {
    try {
      // Clear the currently selected boat if it's no longer available
      const response = await fetch(`${BACKEND_URL}/api/boats`);
      const data = await response.json();
      console.log('Boats data fetched:', data);
      setBoats(data);
      
      // If the currently selected boat no longer exists, deselect it
      if (selectedBoat && !data.some(boat => boat.boatId === selectedBoat)) {
        setSelectedBoat(null);
        setBoatRoute([]);
      }
    } catch (error) {
      console.error('Error fetching boats:', error);
    }
  };

  const fetchBoatRoute = async (boatId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/boats/${boatId}/route?hours=24`);
      const data = await response.json();
      setBoatRoute(data);
    } catch (error) {
      console.error('Error fetching boat route:', error);
    }
  };

  const startTracking = () => {
    if (!currentBoatId) {
      alert('Please enter a boat ID');
      return;
    }
    
    setIsTracking(true);
    
    // Start watching position
    if (navigator.geolocation) {
      const id = navigator.geolocation.watchPosition(
        position => {
          const locationData = {
            boatId: currentBoatId,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            speed: position.coords.speed || 0,
            heading: position.coords.heading || 0
          };
          
          // Send location to server
          if (socket) {
            socket.emit('updateLocation', locationData);
          }
        },
        error => {
          console.error('Error getting location:', error);
          alert('Error tracking location. Please check your GPS permissions.');
          setIsTracking(false);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 5000
        }
      );
      
      // Save the watch ID for cleanup
      setWatchPositionId(id);
    } else {
      alert('Geolocation is not supported by this browser.');
      setIsTracking(false);
    }
  };

  const stopTracking = () => {
    setIsTracking(false);
    
    // Clear geolocation watch
    if (watchPositionId !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchPositionId);
      setWatchPositionId(null);
    }
  };

  const toggleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('viewMode', mode);
  };

  // Determine if we should show mobile view based on device and user preference
  const shouldShowMobileView = viewMode === 'mobile' || (viewMode === 'auto' && isMobile);

  // Add a function to manually refresh boats
  const refreshBoats = () => {
    setLastRefreshTime(Date.now());
  };

  // Helper function to get boat display name
  const getBoatDisplayName = (boatId) => {
    const boat = boats.find(b => b.boatId === boatId);
    return boat ? (boat.displayName || boat.boatId) : boatId;
  };

  // Render the active tab content
  const renderTabContent = () => {
    if (shouldShowMobileView) {
      return (
        <>
          {isTracking ? (
            <div className="tracking-view">
              <div className="status-message">
                <p>आप अभी स्थान भेज रहे हैं: <strong>{getBoatDisplayName(currentBoatId)}</strong></p>
                <p>Sharing location as: <strong>{getBoatDisplayName(currentBoatId)}</strong></p>
              </div>
              <button className="stop-btn" onClick={stopTracking}>रुकें | Stop</button>
            </div>
          ) : (
            <MobileControls 
              boatId={currentBoatId}
              onBoatIdChange={setCurrentBoatId}
              onStartTracking={startTracking}
            />
          )}
        </>
      );
    }

    switch (activeTab) {
      case 'dashboard':
        return <Dashboard onBoatUpdate={refreshBoats} />;
      case 'boats':
        // Pass the refreshBoats function to BoatRegistry to refresh map data after changes
        return <BoatRegistry onBoatUpdate={refreshBoats} />;
      case 'users':
        return <UserManagement />;
      case 'map':
      default:
        return (
          <>
            <div className="sidebar">
              <BoatSelector 
                boats={boats} 
                selectedBoat={selectedBoat} 
                onSelectBoat={setSelectedBoat}
                onRefresh={refreshBoats}
              />
            </div>
            <div className="map-container">
              <MapComponent 
                boats={boats} 
                selectedBoat={selectedBoat}
                boatRoute={boatRoute}
              />
            </div>
          </>
        );
    }
  };

  return (
    <div className="app">
      <header className="app-header">
        <h1>नाव ट्रैकर | Boat Tracker</h1>
        <div className="nav-tabs">
          <button 
            className={activeTab === 'map' ? 'active' : ''} 
            onClick={() => setActiveTab('map')}
          >
            मानचित्र | Map
          </button>
          <button 
            className={activeTab === 'dashboard' ? 'active' : ''} 
            onClick={() => setActiveTab('dashboard')}
          >
            डैशबोर्ड | Dashboard
          </button>
          {isAdmin() && (
            <>
              <button 
                className={activeTab === 'boats' ? 'active' : ''} 
                onClick={() => setActiveTab('boats')}
              >
                नाव रजिस्ट्री | Boats
              </button>
              <button 
                className={activeTab === 'users' ? 'active' : ''} 
                onClick={() => setActiveTab('users')}
              >
                उपयोगकर्ता | Users
              </button>
            </>
          )}
          <button 
            className="logout-btn" 
            onClick={logout}
          >
            लॉगआउट | Logout
          </button>
        </div>
        <div className="view-mode-toggle">
          <button 
            className={viewMode === 'desktop' ? 'active' : ''} 
            onClick={() => toggleViewMode('desktop')}
          >
            डेस्कटॉप मोड | Desktop
          </button>
          <button 
            className={viewMode === 'mobile' ? 'active' : ''} 
            onClick={() => toggleViewMode('mobile')}
          >
            मोबाइल मोड | Mobile
          </button>
        </div>
      </header>
      
      <div className="app-container">
        {renderTabContent()}
      </div>
    </div>
  );
}

function App() {
  const [showRegister, setShowRegister] = useState(false);

  return (
    <AuthProvider>
      <AppContent showRegister={showRegister} setShowRegister={setShowRegister} />
    </AuthProvider>
  );
}

function AppContent({ showRegister, setShowRegister }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!user) {
    return (
      <>
        {showRegister ? (
          <Register onLoginClick={() => setShowRegister(false)} />
        ) : (
          <Login onRegisterClick={() => setShowRegister(true)} />
        )}
        <NetworkStatus />
      </>
    );
  }

  return (
    <>
      <AuthenticatedApp />
      <NetworkStatus />
    </>
  );
}

export default App; 